import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { useSiteSettings } from '@/context/SiteSettingsContext';

const Footer = () => {
  const { t, language } = useLanguage();
  const { settings } = useSiteSettings();

  const footerLinks = {
    services: [
      { href: '/services/company-profile', label: 'Company Profile' },
      { href: '/services/e-commerce', label: 'E-Commerce' },
      { href: '/services/landing-page', label: 'Landing Page' },
      { href: '/services/custom-web-app', label: 'Custom Web App' },
    ],
    company: [
      { href: '/about', label: t('footer.about') },
      { href: '/portfolio', label: 'Portfolio' },
      { href: '/blog', label: 'Blog' },
      { href: '/contact', label: language === 'id' ? 'Kontak' : 'Contact' },
    ],
    legal: [
      { href: '/privacy', label: t('footer.privacy') },
      { href: '/terms', label: t('footer.terms') },
    ],
  };

  return (
    <footer data-testid="footer" className="bg-[#0a0a0a] border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-4">
              {settings.logo_url ? (
                <img 
                  src={settings.logo_url.includes('cloudinary.com')
                    ? settings.logo_url.replace('/upload/', '/upload/w_140,h_140,c_limit,f_auto,q_auto/')
                    : settings.logo_url
                  }
                  alt={settings.site_name || 'Calius Digital'} 
                  width="140"
                  height="40"
                  className="h-10 w-auto object-contain"
                />
              ) : (
                <>
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FF4500] to-[#FF6B35] flex items-center justify-center">
                    <span className="text-white font-bold text-xl">C</span>
                  </div>
                  <span className="text-xl font-bold text-white">{settings.site_name || 'Calius Digital'}</span>
                </>
              )}
            </Link>
            <p className="text-neutral-400 mb-6 max-w-sm">
              {t('footer.description')}
            </p>
            <div className="space-y-3">
              <a href="mailto:hello@calius.digital" className="flex items-center gap-3 text-neutral-400 hover:text-white transition-colors">
                <Mail size={18} />
                <span>hello@calius.digital</span>
              </a>
              <a href="tel:+628126067561" className="flex items-center gap-3 text-neutral-400 hover:text-white transition-colors">
                <Phone size={18} />
                <span>+62 812 6067 561</span>
              </a>
              <div className="flex items-center gap-3 text-neutral-400">
                <MapPin size={18} />
                <span>Indonesia</span>
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold mb-4">{t('footer.services')}</h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-neutral-400 hover:text-white transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-white font-semibold mb-4">{t('footer.company')}</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-neutral-400 hover:text-white transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-white font-semibold mb-4">{t('footer.legal')}</h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-neutral-400 hover:text-white transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-neutral-500 text-sm">
            {t('footer.copyright')}
          </p>
          <div className="flex items-center gap-4">
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-neutral-400 hover:text-white transition-colors">
              Instagram
            </a>
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-neutral-400 hover:text-white transition-colors">
              LinkedIn
            </a>
            <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="text-neutral-400 hover:text-white transition-colors">
              GitHub
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
